import { flushSync } from "react-dom";
import { v4 } from "uuid";
import { VOICE_MODE } from "../../constant/voiceMode";
import { iChatData } from "../../types";
import { getChatId } from "../../utils/chat/chatId";
import { getChatTitle } from "../../utils/chat/chatTitle";
import { getSessionId } from "../../utils/sessionId";
import { dropEmptyContentMessages } from "../../streaming/finalizeGuards";
import { Exchange, joinFragments } from "../../services/voice/liveTranscript";
import type { ShownExchange, Filing, VoiceSession } from "./types";
import { VoiceRuntime, VOICE_EXCHANGE, voiceLog, isRequestRunning } from "./runtime";
import { latestPrompt, voiceUserEntry, gotBusyNote, bubbleText } from "./chatEntries";
import type { TranscriptIO } from "./transcriptIO";
import type { Bubbles } from "./bubbles";
import type { HeardAnswers } from "./heardAnswers";

export const createExchanges = (rt: VoiceRuntime, transcriptIO: TranscriptIO, bubbles: Bubbles, heardAnswers: HeardAnswers) => {
  const { live, sessionRef, turnRef, chatIdRef, deferredRef, emptiedRef, openedFromRef, handlers } = rt;
  const { writeUserLines, adoptChatId, cleanerOf } = transcriptIO;
  const { applyTargets, openChat, nothingAhead } = bubbles;
  const { settleAnswer } = heardAnswers;

  const appendExchange = (
    user: string,
    reply: string,
    liveSessionId: string | null,
    callXenia: boolean,
    shownId: string | null,
    hideReply: boolean,
  ) => {
    const { state: current, history: historyCtx, handleOpenSocketConnection: openTurn } = live.current;
    const empty = openChat();
    adoptChatId(liveSessionId);
    const prior = dropEmptyContentMessages(latestPrompt(current.chatData));
    const traceId = v4().slice(0, 8);
    const sessionMark = liveSessionId ? { liveSessionId } : {};
    const userEntry = shownId || !user ? null : voiceUserEntry(user, liveSessionId);
    const ids = { userId: userEntry?.id ?? shownId ?? "", replyId: v4() };
    current.setChatData((prev: iChatData[]) => [
      ...prev,
      ...(userEntry ? [userEntry] : []),
      {
        id: ids.replyId,
        message: reply,
        isUserMessage: false,
        prompt: [...prior, ...(user ? [{ role: "user", content: user }] : []), { role: "assistant", content: reply }],
        isLoading: false,
        destinationList: [],
        singleSelectedDestination: false,
        responseType: "ai_assistant",
        voiceReply: reply,
        traceId,
        ...sessionMark,
        ...(hideReply ? { shouldNotShowFirstMessage: true } : {}),
      },
    ]);
    historyCtx.setCallSaveHistoryAPIFlag({ flag: true, method: empty ? "POST" : "PUT" });
    voiceLog("exchange.voice-only", { traceId, callXenia, userChars: user.length, replyChars: reply.length });
    if (!callXenia) return ids;
    openTurn({
      requestType: VOICE_EXCHANGE,
      userInput: user,
      voiceReply: reply,
      messages: prior,
      chatId: getChatId(),
      chatTitle: getChatTitle(current.chatData),
      sessionId: getSessionId(),
      userId: current.userInfo?.id ? String(current.userInfo.id) : "",
      traceId,
    });
    return ids;
  };

  const trackExchange = (
    session: VoiceSession,
    exchange: Exchange,
    ids: { userId: string; replyId: string },
    shownId: string | null,
    user: string,
    reply: string,
  ) => {
    session.targets = session.targets.filter((target) => target.entryId !== ids.userId);
    session.targets.push(
      { fragments: exchange.user, entryId: ids.userId, sent: user, shown: shownId ? null : user, filed: true },
      { fragments: exchange.reply, entryId: ids.replyId, sent: reply, shown: reply, filed: true },
    );
    applyTargets(session);
  };

  const noteHeld = (exchange: Exchange, ids: { userId: string; replyId: string }) => {
    const turn = turnRef.current;
    if (turn && exchange.user.some((fragment) => turn.heard.includes(fragment))) turn.held = ids;
  };

  const flushDeferred = () => {
    if (isRequestRunning() || deferredRef.current.length === 0) return;
    if (live.current.state.chatData.some((entry: iChatData) => entry.isLoading)) return;
    const item = deferredRef.current.shift();
    if (item) {
      const hidden = gotBusyNote(item.session, item.exchange);
      const ids = appendExchange(item.user, item.reply, item.liveSessionId, false, item.shownId, hidden);
      item.session.stats.voiceOnly += 1;
      if (hidden) noteHeld(item.exchange, ids);
      trackExchange(item.session, item.exchange, ids, item.shownId, item.user, item.reply);
    }
    if (deferredRef.current.length > 0) setTimeout(() => handlers.current.flushDeferred(), 50);
  };

  const resolveExchange = (session: VoiceSession, exchange: Exchange) => {
    writeUserLines(session, exchange.user, "gpt-live", null, null);
    const user = joinFragments(exchange.user);
    const reply = joinFragments(exchange.reply);
    const shownId = session.earlyUserId;
    session.earlyUserId = null;
    const liveId = session.liveReply?.fragments === exchange.reply ? session.liveReply.entryId : null;
    if (liveId) session.liveReply = null;
    if (!user && reply) voiceLog("exchange.orphan", { liveSessionId: session.id, replyChars: reply.length, preview: reply.slice(0, 80) });
    if (!reply) return;
    const cleaner = cleanerOf(session);
    cleaner.finish("user", exchange.user);
    cleaner.finish("assistant", exchange.reply);
    const filing: Filing = { exchange, shownId, shown: null, orphan: !user, liveId };
    const show = !!liveId || nothingAhead(session);
    session.filings.push(filing);
    if (show) showExchange(session, filing);
    fileExchanges(session, false);
  };

  const showExchange = (session: VoiceSession, filing: Filing) => {
    const { state: current } = live.current;
    const cleaner = cleanerOf(session);
    const { exchange, shownId, orphan, liveId } = filing;
    const user = cleaner.best(exchange.user);
    const reply = cleaner.best(exchange.reply);
    const empty = current.chatData.length === 0;
    const prior = dropEmptyContentMessages(latestPrompt(current.chatData));
    const held = gotBusyNote(session, exchange);
    const userEntry = shownId || orphan ? null : voiceUserEntry(bubbleText(cleaner, exchange.user), session.id);
    const ids = { userId: userEntry?.id ?? shownId ?? "", replyId: liveId ?? v4() };
    const traceId = v4().slice(0, 8);
    const replyEntry = {
      id: ids.replyId,
      message: bubbleText(cleaner, exchange.reply),
      isUserMessage: false,
      prompt: [...prior, ...(orphan ? [] : [{ role: "user", content: user }]), { role: "assistant", content: reply }],
      isLoading: false,
      destinationList: [],
      singleSelectedDestination: false,
      responseType: "ai_assistant",
      traceId,
      ...(session.id ? { liveSessionId: session.id } : {}),
      ...(held ? { shouldNotShowFirstMessage: true } : {}),
    };
    const commit = sessionRef.current === session ? flushSync : (run: () => void) => run();
    commit(() => {
      openChat();
      adoptChatId(session.id);
      current.setChatData((prev: iChatData[]) =>
        liveId
          ? prev.map((entry) => (entry.id === liveId ? { ...entry, ...replyEntry } : entry))
          : [...prev, ...(userEntry ? [userEntry] : []), replyEntry],
      );
    });
    filing.shown = { ...ids, traceId, empty, held, early: !!shownId, orphan };
    if (held) noteHeld(exchange, ids);
    session.targets = session.targets.filter((target) => target.entryId !== ids.userId && target.entryId !== ids.replyId);
    session.targets.push(
      ...(orphan ? [] : [{ fragments: exchange.user, entryId: ids.userId, sent: user, shown: null, filed: false }]),
      { fragments: exchange.reply, entryId: ids.replyId, sent: reply, shown: null, filed: false },
    );
    applyTargets(session);
    voiceLog("exchange.shown", { traceId, userChars: user.length, replyChars: reply.length });
  };

  const fileShownExchange = (session: VoiceSession, exchange: Exchange, shown: ShownExchange, user: string, reply: string) => {
    const { state: current, history: historyCtx, handleOpenSocketConnection: openTurn } = live.current;
    const entry = current.chatData.find((candidate: iChatData) => candidate.id === shown.replyId);
    const ours = (id: string | null) => id === shown.replyId || (!shown.early && !shown.orphan && id === shown.userId);
    const running = isRequestRunning();
    if (!entry || !reply || (!user && !shown.orphan)) {
      session.targets = session.targets.filter((target) => !ours(target.entryId));
      if (!entry) return;
      emptiedRef.current = session;
      current.setChatData((prev: iChatData[]) => prev.filter((candidate) => !ours(candidate.id)));
      if (!running) historyCtx.setCallSaveHistoryAPIFlag({ flag: true, method: "PUT" });
      return;
    }
    const prior = Array.isArray(entry.prompt) ? entry.prompt.slice(0, shown.orphan ? -1 : -2) : [];
    current.setChatData((prev: iChatData[]) =>
      prev.map((candidate) => (candidate.id === shown.replyId ? { ...candidate, voiceReply: reply } : candidate)),
    );
    session.targets.forEach((target) => {
      if (target.entryId === shown.userId || target.entryId === shown.replyId) target.filed = true;
    });
    applyTargets(session);
    if (!running) historyCtx.setCallSaveHistoryAPIFlag({ flag: true, method: shown.empty ? "POST" : "PUT" });
    voiceLog("exchange.voice-only", { traceId: shown.traceId, callXenia: !shown.held && !running, userChars: user.length, replyChars: reply.length });
    session.stats.voiceOnly += 1;
    if (shown.held) return;
    session.lastExchange = shown.orphan ? null : { fragments: exchange.user, userId: shown.userId, replyId: shown.replyId };
    if (running) return;
    openTurn({
      requestType: VOICE_EXCHANGE,
      userInput: user,
      voiceReply: reply,
      messages: prior,
      chatId: getChatId(),
      chatTitle: getChatTitle(current.chatData),
      sessionId: getSessionId(),
      userId: current.userInfo?.id ? String(current.userInfo.id) : "",
      traceId: shown.traceId,
    });
  };

  const reopenLine = (session: VoiceSession, exchange: Exchange, shown: ShownExchange) => {
    const after = [...session.transcript.pendingFragments()];
    session.transcript.reopen(exchange.user);
    cleanerOf(session).forget(after);
    const line = session.transcript.pendingFragments();
    session.earlyUserId = shown.userId;
    session.targets = session.targets.filter((target) => target.entryId !== shown.userId && target.entryId !== shown.replyId);
    session.targets.push({ fragments: line, entryId: shown.userId, sent: "", shown: null, filed: false });
    live.current.state.setChatData((prev: iChatData[]) => prev.filter((entry) => entry.id !== shown.replyId));
    applyTargets(session);
    voiceLog("exchange.reopened", { traceId: shown.traceId, userFragments: exchange.user.length, after: after.length });
  };

  const fileExchanges = (session: VoiceSession, force: boolean) => {
    const cleaner = cleanerOf(session);
    while (session.filings.length > 0) {
      const { exchange, shownId, shown, orphan } = session.filings[0];
      const user = cleaner.cleaned(exchange.user);
      const reply = cleaner.cleaned(exchange.reply);
      if (!force && !(user.complete && reply.complete)) return;
      session.filings.shift();
      const userText = user.complete ? user.text : cleaner.best(exchange.user);
      const replyText = reply.complete ? reply.text : cleaner.best(exchange.reply);
      if (shown && !force && !replyText && userText && !shown.held && !shown.orphan && !isRequestRunning()) {
        reopenLine(session, exchange, shown);
        continue;
      }
      if (shown) {
        fileShownExchange(session, exchange, shown, userText, replyText);
        continue;
      }
      if (!replyText || (!userText && !orphan)) continue;
      if (isRequestRunning()) {
        deferredRef.current.push({ user: userText, reply: replyText, liveSessionId: session.id, shownId, session, exchange });
        continue;
      }
      const held = gotBusyNote(session, exchange);
      const ids = appendExchange(userText, replyText, session.id, !held, shownId, held);
      session.stats.voiceOnly += 1;
      if (held) noteHeld(exchange, ids);
      else session.lastExchange = orphan ? null : { fragments: exchange.user, ...ids };
      trackExchange(session, exchange, ids, shownId, userText, replyText);
    }
  };

  const settleExchange = (session: VoiceSession, at: number) => {
    if (at - (session.lastAssistantAt ?? at) < VOICE_MODE.exchangeSettleMs) return;
    if (session.answer && session.answer.fragments.length > 0) settleAnswer(session);
    if (session.handOff) return;
    const held = session.transcript.settleBusy();
    if (held) resolveExchange(session, held);
    if (!session.transcript.hasReply()) return;
    const exchange = session.transcript.settle();
    if (exchange) resolveExchange(session, exchange);
  };

  const keepEmptied = (empty: boolean): boolean => {
    const emptied = emptiedRef.current;
    emptiedRef.current = null;
    if (!empty || !emptied || emptied !== sessionRef.current) return false;
    chatIdRef.current = "";
    const opened = openedFromRef.current?.session === emptied ? openedFromRef.current : null;
    openedFromRef.current = null;
    const { state: current, location: where, navigate: go } = live.current;
    if (opened && current.isMobile && opened.path !== where.pathname) go(opened.path);
    if (opened && !current.isMobile) {
      current.setFirstScreen(opened.firstScreen);
      current.setScreen(opened.screen);
    }
    voiceLog("chat.emptied", { restored: !!opened, liveSessionId: emptied.id });
    return true;
  };

  return { appendExchange, trackExchange, noteHeld, flushDeferred, resolveExchange, showExchange, fileShownExchange, reopenLine, fileExchanges, settleExchange, keepEmptied };
};

export type Exchanges = ReturnType<typeof createExchanges>;
